import { apiFetch } from '@connectors/newport/swClient';
import { GetServerSidePropsContext } from 'next';

class ShopwareClient {
    constructor(request) {
        this.request = request;
    }

    createCacheKey(string) {
        const req = this.request;
        return `sw-client:${string}:${req.currentDomainConfig.salesChannelId}:${req?.buildId}`;
    }

    async getStaticData() {
        const TTL = 3600 * 3;
        const cacheKey = this.createCacheKey('static-product-data');
        const req = this.request;
        const result = await this.request.cacheManager.get(cacheKey);

        if (result) {
            return JSON.parse(result);
        }

        const response = await apiFetch('product/static-data', 'get', null, this.request);
        const result2 = response?.data;
        if (response.status === 'success' && result2) {
            try {
                req.cacheManager.set(cacheKey, JSON.stringify(result2), TTL);
            } catch (e) {
                console.error('Redis failed to set the value for static product data.');
            }
        }

        return result2;
    }

    async getNavigationSeoPathsFromIds(ids) {
        const criteria = {
            filter: [
                {
                    type: 'equalsAny',
                    field: 'pathInfo',
                    value: ids.map((item) => {
                        return `/navigation/${item}`;
                    }),
                },
            ],
            sort: [
                {
                    field: 'isCanonical',
                },
            ],
        };

        const response = await apiFetch('seo-url', 'POST', criteria, this.request);

        if (response.status === 'success') {
            return response?.data?.elements;
        }

        return [];
    }

    async getSeoPath(path) {
        const TTL = 3600 * 12;
        const cacheKey = this.createCacheKey(path);
        const req = this.request;
        const result = await this.request.cacheManager.get(cacheKey);

        if (result) {
            return JSON.parse(result);
        }

        const response = await apiFetch('seo-url', 'POST', {
            filter: [
                {
                    type: 'equals',
                    field: 'seoPathInfo',
                    value: path,
                },
            ],
            limit: 5,
            page: 1,
            sort: [
                {
                    field: 'isCanonical',
                },
            ],
        }, this.request);

        const result2 = response?.data?.elements?.[0];
        if (response.status === 'success' && result2) {
            try {
                req.cacheManager.set(cacheKey, JSON.stringify(result2), TTL).then((res) => null);
            } catch (e) {
                console.error('Redis failed to set the value for seo path:', path);
            }
        }

        return result2;
    }

    /**
     * @param context {GetServerSidePropsContext}
     * @returns {Promise<{status: string, order: *}|{status: string, errors: ({headers: {'sw-context-token': *}, data: *, status: string, statusCode: *}|{type, error: string, status: string, statusCode}|{type, error: *, status: string, statusCode: *})}>}
     */
    async getOrderWithServerSideContext(context) {
        const orderId = context.query.orderId;

        let pageRes = await apiFetch(process.env.STOREFRONT_CHECKOUT_ORDER_ENDPOINT, 'post', {
            associations: {
                lineItems: {},
                transactions: {},
                currency: {},
            },
            filter: [
                {
                    type: 'equals',
                    field: 'id',
                    value: orderId,
                },
            ],
            limit: 1,
        }, context.req);

        if (pageRes.status === 'error') {
            pageRes?.error?.errors.forEach((error) => {
                console.log(error.meta);
            });

            return {
                status: 'error',
                errors: pageRes,
            };
        }

        return {
            status: 'success',
            order: pageRes?.data?.orders?.elements[0],
        };
    }

    /**
     * @param context {GetServerSidePropsContext}
     * @returns {Promise<{paymentMethods: *}>}
     */
    async getPaymentMethods(context) {
        let pageRes = await apiFetch('payment-method', 'post', {}, context.req);

        if (pageRes.status === 'error') {
            return {
                status: 'error',
            };
        }

        return { paymentMethods: pageRes?.data?.elements };
    }
}

export default ShopwareClient;
